import { createContext, useContext } from 'react';
import type { CropSpec } from '../utils/editRecipe';

/** Print sizes and finishes, mirroring `loupe_core::db::FINISHES`. */
export const PRINT_SIZES = ['4x6', '6x8'] as const;
export const PRINT_FINISHES = ['gloss', 'matte', 'fine_matte', 'lustre'] as const;

export const DEFAULT_SIZE = '4x6';
export const DEFAULT_FINISH = 'gloss';
/** Prints are borderless unless the customer asks for a border. */
export const DEFAULT_BORDER = false;

/** Human-readable finish label (`fine_matte` → `Fine Matte`). */
export function finishLabel(finish: string): string {
  return finish
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** Metadata about a scan, supplied by the gallery when selecting for print. */
export interface ScanMeta {
  scan_id: string;
  filename: string;
  thumbnail_url: string;
  roll_id?: string;
  /**
   * The gallery this scan belongs to. Not persisted — like the filename and
   * thumbnail it is re-derived whenever a selection is hydrated, so it is
   * absent only in the moment between restoring a selection and hydrating it.
   */
  order_id?: string;
}

/**
 * One print line item. A scan may appear in multiple lines under different
 * (size, finish, border) combinations, so each line has its own stable `id`.
 */
export interface PrintLine extends ScanMeta {
  id: string;
  size: string;
  finish: string;
  /** Print with an approximately 5mm white border around the image. */
  border: boolean;
  /**
   * How the frame is cropped to the paper's aspect. `null` — the default —
   * means letterbox the whole frame, losing nothing. A crop only ever exists
   * because the customer framed one.
   */
  crop: CropSpec | null;
  quantity: number;
}

export interface LinePatch {
  size?: string;
  finish?: string;
  border?: boolean;
  /** Pass `null` to return the line to the whole-frame letterbox. */
  crop?: CropSpec | null;
  quantity?: number;
}

/** The subset of a line the bulk "apply to all / apply to roll" controls set. */
export interface BulkPatch {
  size?: string;
  finish?: string;
  border?: boolean;
}

export interface PrintSelectionContextValue {
  lines: PrintLine[];
  totalQuantity: number;
  /** Is any line present for this scan? (gallery checkbox state) */
  isScanSelected: (scanId: string) => boolean;
  /** Add one default line for a scan if none exists; else remove all its lines. */
  toggleScan: (meta: ScanMeta) => void;
  /** Add another line for a scan (e.g. a second size/finish) in the basket. */
  addLine: (meta: ScanMeta, size?: string, finish?: string, border?: boolean) => void;
  removeLine: (id: string) => void;
  updateLine: (id: string, patch: LinePatch) => void;
  /** Add default lines for every scan in a roll that isn't already selected. */
  selectRoll: (metas: ScanMeta[]) => void;
  /** Remove all lines for the given scans (deselect a roll). */
  deselectScans: (scanIds: string[]) => void;
  /** Bulk-apply size/finish/border to every line in a roll. */
  applyToRoll: (rollId: string, patch: BulkPatch) => void;
  /** Bulk-apply size/finish/border to every line. */
  applyToAll: (patch: BulkPatch) => void;
  clearSelection: () => void;
  /**
   * Fill in filename/thumbnail/roll for lines restored from the server (which
   * only persists scan_id/size/finish/quantity) using freshly-loaded gallery
   * scans. Signed thumbnail URLs expire, so they are never persisted — the
   * gallery re-hydrates them each visit.
   */
  hydrateFromScans: (metas: ScanMeta[]) => void;
}

export const PrintSelectionContext = createContext<PrintSelectionContextValue | null>(null);

export function usePrintSelection(): PrintSelectionContextValue {
  const context = useContext(PrintSelectionContext);
  if (!context) {
    throw new Error('usePrintSelection must be used within a PrintSelectionProvider');
  }
  return context;
}
